import {useCallback, useState} from 'react';
import {useApiMutation} from '@legit.health/backend-client';
import {
  base64ToBlob,
  blobToBase64,
  CropperAspect,
  ImageDropzone,
  ImageValue,
  ImageWithCrop
} from '@legit.health/ui';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import {Backdrop, Box, CircularProgress, Typography} from '@mui/material';
import * as Sentry from '@sentry/react';
import useImageMaxFileSize from '@/shared/hooks/useImageMaxFileSize';

export default function ImageField({
  onChange,
  onCompressionError,
  value,
  name,
  placeholderImage,
  sizeErrorMessage,
  generalErrorMessage,
  typeErrorMessage,
  dataTestId,
  label,
  allowGallery = true
}: {
  value: ImageValue;
  onChange: (value: ImageWithCrop | null) => void;
  onCompressionError?: (error: string) => void;
  name: string;
  placeholderImage: string;
  label: string;
  sizeErrorMessage: string;
  generalErrorMessage: string;
  typeErrorMessage: string;
  dataTestId?: string;
  allowGallery?: boolean;
}) {
  const customMaxFileSize = useImageMaxFileSize();
  const [isCompressing, setIsCompressing] = useState(false);

  const {mutateAsync: compressImage} = useApiMutation<{
    data: string;
    dimensions: {width: number; height: number};
  }>();

  const handleSelectImage = useCallback(
    async function (imageWithCrop: ImageWithCrop | null, type: 'crop' | 'add' | 'remove') {
      // Only compress on new image add, not on crop adjustments or removal
      if (type !== 'add') {
        onChange(imageWithCrop);
        return;
      }

      if (!imageWithCrop?.file) {
        onChange(imageWithCrop);
        return;
      }

      setIsCompressing(true);
      try {
        const base64Image = await blobToBase64(imageWithCrop.file);
        const {data: compressedImage} = await compressImage({
          path: '/compress-image',
          method: 'POST',
          body: {image: base64Image}
        });
        const formattedBase64 = `data:image/jpg;base64,${compressedImage.data}`;
        const blob = await base64ToBlob(formattedBase64);

        onChange({
          ...imageWithCrop,
          crop: {
            x: 0,
            y: 0,
            width: compressedImage.dimensions.width,
            height: compressedImage.dimensions.height
          },
          file: blob
        });
      } catch (error) {
        Sentry.captureException(error);
        onCompressionError?.('Failed to compress image');
        onChange(imageWithCrop);
      } finally {
        setIsCompressing(false);
      }
    },
    [compressImage, onChange, onCompressionError]
  );

  return (
    <div>
      <Box
        sx={{
          mt: {
            xs: 2,
            lg: 0
          }
        }}
      >
        <ImageDropzone
          containerSx={{
            borderColor: 'primary.main',
            borderRadius: 1,
            p: {
              xs: 2,
              lg: 0
            }
          }}
          onChange={handleSelectImage}
          withCropper
          cropperAspect={CropperAspect.FREE}
          {...(customMaxFileSize && {maxFileSize: customMaxFileSize})}
          sizeErrorMessage={sizeErrorMessage}
          generalErrorMessage={generalErrorMessage}
          typeErrorMessage={typeErrorMessage}
          placeholder={
            <Box
              sx={{
                position: 'relative',
                '& img': {
                  display: 'block',
                  width: '100%',
                  height: 'auto'
                }
              }}
            >
              <AddAPhotoIcon
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  margin: 'auto',
                  color: 'primary.main',
                  fontSize: '4rem'
                }}
              />

              <img src={placeholderImage} alt={name} />
              <Typography
                sx={{
                  mt: 1,
                  color: 'primary',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}
              >
                {label}
              </Typography>
            </Box>
          }
          value={value}
          dataTestId={dataTestId}
          allowGallery={allowGallery}
        />
      </Box>
      <Backdrop
        sx={(theme) => ({color: '#fff', zIndex: theme.zIndex.drawer + 1})}
        open={isCompressing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}
